import { UseQueryResult } from "@tanstack/react-query";

import LabelBadge from "./LabelBadge";

import PaginatedSelect, { SelectedValue } from "@/components/ui/PaginatedSelect";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptVersions } from "@/queries";
import { ListPromptVersion } from "@/types";

interface PromptVersionSelectorProps {
  promptId: number;
  selectedVersion?: {
    id?: string | number;
    number: string | number;
  } | null;
  onValueChange: (version: ListPromptVersion) => void;
  onRemove?: () => void;
  showLabel?: boolean;
}

const defaultOption = {
  id: -1,
  number: -1,
  name: "Latest (default)",
};

const PromptVersionSelector = ({
  promptId,
  selectedVersion,
  onValueChange,
  onRemove,
  showLabel = true,
}: PromptVersionSelectorProps) => {
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const userToken = useAuth()?.userToken ?? "";

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const Query = (page: number): UseQueryResult<any, unknown> => {
    const result = usePromptVersions(userToken || "", {
      promptRegistryId: promptId,
      workspaceId: workspaceId,
      page: page,
      perPage: 10,
    });

    return result;
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const extractVersionsData = (data: any) => {
    if (!data) {
      return { data: [defaultOption], totalPages: 0 };
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const items = data.pages.flatMap((page: any) => page.prompt_versions);
    const totalPages = data.pages[0]?.pages || 0;

    // Filter out archived versions
    const activeVersions = items.filter((item: ListPromptVersion) => !item.archived);

    return {
      data: [defaultOption, ...activeVersions],
      totalPages,
    };
  };

  const handleVersionChange = (value: SelectedValue<ListPromptVersion>) => {
    if (value.id === "-1") {
      onRemove?.();
      return;
    }
    onValueChange(value.item);
  };

  return (
    <div>
      {showLabel && (
        <label htmlFor="promptVersion" className="mb-1 block text-sm font-medium">
          Prompt Version
        </label>
      )}
      <div className="flex flex-row items-center gap-x-2 overflow-visible">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <PaginatedSelect<ListPromptVersion, any>
          query={Query}
          renderItem={item => (
            <div className="flex flex-col">
              <span className="font-medium">
                {item.number === -1 ? "Latest (default)" : `Version ${item.number}`}
              </span>
              <span className="text-xs text-gray-500">{item.commit_message}</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {Array.isArray(item.labels) &&
                  item.labels.map((label, index) => (
                    <LabelBadge
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      label={label.name}
                      variant="environment"
                    />
                  ))}
              </div>
            </div>
          )}
          getItemValue={item => item.number.toString()}
          value={
            selectedVersion
              ? ({
                  id: selectedVersion.number.toString(),
                  item: {
                    id: (selectedVersion.id || selectedVersion.number).toString(),
                    number: selectedVersion.number,
                    name: `Version ${selectedVersion.number}`,
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any)
              : ({
                  id: defaultOption.number.toString(),
                  item: defaultOption,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any)
          }
          onValueChange={handleVersionChange}
          extractData={extractVersionsData}
        />
      </div>
    </div>
  );
};

export default PromptVersionSelector;
